import type { ContratProADTO, LovProADTO } from '@/features/api/extranet/dto/Demande/PROA'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import ContratStep from '@/features/interface/demande/proa/steps/contrat/ContratStep.vue'
import DocumentsStep from '@/features/interface/demande/proa/steps/documents/DocumentsStep.vue'
import EmployeurStep from '@/features/interface/demande/proa/steps/employeur/EmployeurStep.vue'
import FormationStep from '@/features/interface/demande/proa/steps/formation/FormationStep.vue'
import IntroductionStep from '@/features/interface/demande/proa/steps/introduction/IntroductionStep.vue'
import SalarieStep from '@/features/interface/demande/proa/steps/salarie/SalarieStep.vue'
import SyntheseStep from '@/features/interface/demande/proa/steps/synthese/SyntheseStep.vue'
import { PROA_ACTIONS } from '@/features/interface/demande/proa/useDemandePROAActions'
import { useDemandePROAActions } from '@/features/interface/demande/proa/useDemandePROAActions.ts'
import type { SetupDemande } from '@/features/interface/demande/shared/components/demande-content/DemandeContent.types.ts'
import { storeToRefs } from 'pinia'
import { type Component, provide } from 'vue'
import FinancementStep from './steps/financement/FinancementStep.vue'
import { PROA_MODULES_ACTIONS, useModuleActions } from './steps/formation/useModuleActions'
import { useDemandePROAStore } from './useDemandePROAStore'
import { DEMANDE_PRINT_ACTIONS, type DemandeStore } from '@/features/interface/demande/shared'
import { useDemandePROAPrint } from '@/features/interface/demande/proa/useDemandePROAPrint.ts'

type TabKey =
  | 'Introduction'
  | 'Employeur'
  | 'Salarié'
  | 'Contrat'
  | 'Formation'
  | 'Financement'
  | 'Synthèse'
  | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionStep,
  Employeur: EmployeurStep,
  Salarié: SalarieStep,
  Contrat: ContratStep,
  Formation: FormationStep,
  Financement: FinancementStep,
  Synthèse: SyntheseStep,
  Documents: DocumentsStep,
} as const

function customizeNatureFichiers(store: DemandeStore<'MODEL_PROA', LovProADTO>) {
  const { lov } = storeToRefs(store)
  return lov.value.NatureFichier.map((item) => {
    switch (item.code) {
      case 'AttestationFormateurInterne': {
        return {
          code: 'AttestationFormateurInterne',
          intitule: 'Attestation Service Formation interne + CV des formateurs',
        }
      }
      case 'Convention': {
        return {
          code: 'Convention',
          intitule: 'Convention signée des 2 parties',
        }
      }
      default: {
        return item
      }
    }
  })
}

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ContratProADTO> = {
  Introduction: { include: (state) => [state.validationNotice] },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Salarié: {
    includeDeep: (state) => [state.salarie, state.tutorat],
  },
  Contrat: {
    includeDeep: (state) => [state.contrat],
    include: (state) => [state.dispositif],
  },
  Formation: { includeDeep: (state) => [state.formation] },
  Financement: { includeDeep: (state) => [state.financement] },
  Synthèse: {
    include: (state) => [
      state.lieuSignature,
      state.commentaire,
      state.acceptationCg,
      state.attestationSurHonneur,
      state.dateSignature,
    ],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
} as const

export const SetupDemandePROA: SetupDemande<'MODEL_PROA', TabKey> = {
  useStore: useDemandePROAStore,
  injectActions: () => {
    const actions = useDemandePROAActions()
    provide(PROA_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (store) => {
    const { typeContextCourant, estTT } = storeToRefs(store)
    if (estTT.value && typeContextCourant.value === TypesContextesAutorisesEnum.Adherent)
      return TABS_ERR_CONFIG

    const { Financement: _, ...newTabs } = TABS_ERR_CONFIG
    return newTabs
  },
  customizeNatureFichiers,
  onInitialize: (store) => {
    provide(PROA_MODULES_ACTIONS, useModuleActions())
    provide(DEMANDE_PRINT_ACTIONS, useDemandePROAPrint(store))
  },
} as const
